import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Grid,
  Checkbox,
  Message,
  Popup,
} from "semantic-ui-react";
import { useMessageResources, useTextResources } from "../../language";
import PropTypes from "prop-types";
import GridPanel from "../../components/GridPanel";
import TextInput from "../../components/shared/TextInput";
import NumericInput from "../../components/shared/NumericInput";
import {
  PricingTemplatesPrices as PricingTemplatesPricesApi,
  PricingTemplates as PricingTemplatesApi,
} from "../../api/services/next";
import {
  getCombinationsPossibilities,
} from "../../components/shared/utils";
import {
  onError,
  onSuccess,
  onWarning,
} from "../../components/shared/messages";
import "./style.css";
import { useConfirm } from "../../hooks/useConfirm";
import { onUpdateGridHandler } from "../../components/GridPanel/utility";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import { useLoader } from "../../hooks/useLoader";
import CheckBoxToggle from "../../components/shared/CheckBoxToggle";

const propertiesEnum = {
  Adults: "Adults",
  Children: "Children",
  PriceAdjustment: "PriceAdjustment",
};

// Sort prices by PriceAdjustment (Percent) descending
const sortByPriceAdjustment = (prices) => {
  return [...prices].sort(
    (a, b) => (b.PriceAdjustment || 0) - (a.PriceAdjustment || 0)
  );
};

const TemplateEditor = ({
  onCancelChanges,
  open,
  extendedProps: { setRowName, row, setTemplates, onClose, isEditModalOpen },
}) => {
  const [pricingTemplatesPrices, setPricingTemplatesPrices] = useState([]);
  const [rowNameLocal, setRowNameLocal] = useState(row.Name);
  const [isActive, setIsActive] = useState(row.IsActive ?? true);
  const text = useTextResources();
  const message = useMessageResources();
  const loader = useLoader();
  const confirm = useConfirm();
  const [selected, setSelected] = useState([]);
  const currentHotel = useCurrentHotel();
  const [isAddingRows, setIsAddingRows] = useState(false);
  const isOpenModal =
    open /* create from grid */ || isEditModalOpen; /* custom managed */

  useEffect(() => {
    if (!row.Id) return;
    loader(true);
    PricingTemplatesPricesApi.GetPricingTemplatesPrices({
      TemplateId: row.Id,
    })
      .then((prices) => setPricingTemplatesPrices(sortByPriceAdjustment(prices)))
      .catch((error) => onError(error))
      .finally(loader(false));
  }, [row.Id]);

  useEffect(() => {
    setRowNameLocal(row.Name);
  }, [row.Name]);

  useEffect(() => {
    setIsActive(row.IsActive ?? true);
  }, [row.IsActive]);

  useEffect(() => {
    setPricingTemplatesPrices([]);
  }, [row.Id]);

  const changePricingRow = (property, id, newValue) => {
    setPricingTemplatesPrices((prices) =>
      prices.map((price) =>
        price.Id !== id
          ? price
          : {
              ...price,
              [property]:
                property === propertiesEnum.PriceAdjustment
                  ? (typeof newValue === "number"
                      ? newValue
                      : newValue === null || newValue === ""
                        ? null
                        : parseFloat(newValue))
                  : typeof newValue === "number"
                    ? parseInt(newValue)
                    : newValue,
              BasePriceForChildrenInSeparateRoomCalculation:
                price.Adults == 0 && //previous value
                newValue !== 0 &&
                property == "Adults" &&
                price.BasePriceForChildrenInSeparateRoomCalculation
                  ? false
                  : price.BasePriceForChildrenInSeparateRoomCalculation,
            }
      )
    );
  };

  const onSave = async () => {
    try {
      if (isAddingRows) {
        onWarning(message("SaveTemplateRowBefore"));
        return;
      }

      if (!pricingTemplatesPrices?.length) {
        onWarning(message("AddAtLeastOneTemplateRow"));
        return;
      }

      loader(true);

      const combinationExists = pricingTemplatesPrices.some(
        (obj, index, arr) =>
          arr.findIndex(
            (o) => o.Adults === obj.Adults && o.Children === obj.Children
          ) !== index
      );

      if (combinationExists) {
        onWarning(message("CombinationAlreadyExistPleaseChangeExistingOne"));
        return;
      }

      if (!rowNameLocal) {
        onWarning(message("TemplateNameIsARequiredField"));
        return;
      }

      const isNewTemplate = Boolean(!row.Id);
      const saveTemplatePromise = PricingTemplatesApi.SavePricingTemplates([
        {
          ...row,
          Id: isNewTemplate ? 0 : row.Id,
          Name: rowNameLocal,
          IsActive: isActive,
          ChainId: currentHotel.HotelChainId,
        },
      ]);
      const savedTemplates = await saveTemplatePromise;

      const toSave = pricingTemplatesPrices.map((p) => {
        const isNew = typeof p.Id == "string";
        return {
          ...p,
          Id: isNew ? 0 : p.Id,
          TemplateId: isNew ? savedTemplates[0].Id : p.TemplateId,
        };
      });
      const savedPrices =
        await PricingTemplatesPricesApi.SavePricingTemplatesPrices(toSave);

      setPricingTemplatesPrices(sortByPriceAdjustment(savedPrices));
      setRowName(savedTemplates[0].Name, row.Id || savedTemplates[0].Id);
      if (isNewTemplate) {
        setTemplates((prev) => [...savedTemplates, ...prev]);
      } else {
        setTemplates((prev) =>
          prev.map((t) => t.Id === savedTemplates[0].Id ? savedTemplates[0] : t)
        );
      }
      onSuccess();
    } catch (error) {
      onError(error);
    } finally {
      loader(false);
    }
  };

  const showConfirmationDialog = async ({ header, content }) => {
    return new Promise((resolve) => {
      confirm({
        header: message(header),
        content: message(content),
        confirmButton: {
          content: text("Yes"),
          positive: true,
        },
        cancelButton: {
          content: text("No"),
          negative: true,
        },
        onConfirm: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
      });
    });
  };

  const onGridUpdate = async (added, changed, deleted) => {
    let copy = [...pricingTemplatesPrices];
    let updated = onUpdateGridHandler(
      pricingTemplatesPrices,
      added,
      changed,
      deleted
    );
    if (added) {
      const newItem = {
        ...added[0],
        TemplateId: row.Id,
        Id: `New-Item-${crypto.randomUUID()}`,
      };

      const combinationExists = copy.some(
        ({ Adults, Children }) =>
          Adults === newItem.Adults && Children === newItem.Children
      );

      if (combinationExists) {
        onWarning(message("CombinationAlreadyExistPleaseChangeExistingOne"));
        return;
      }

      if (added.some((a) => !a.PriceAdjustment)) {
        const confirmed = await showConfirmationDialog({
          header: "AreYouSure",
          content: "YouDidNotSetThePercentsAreYouSureYouWouldLikeToProceed",
        });

        if (!confirmed) {
          return;
        }
      }

      copy = [newItem, ...copy];
    }

    if (selected.length) {
      const selectedIdsForDeletion = selected.map((index) => copy[index].Id);
      const confirmed = await showConfirmationDialog({
        header: "AreYouSure",
        content: "AreYouSureYouWouldLikeToDeleteSelectedItems",
      });

      if (confirmed) {
        try {
          loader(true);
          const selectedIdsWithoutNewly = selectedIdsForDeletion.filter(
            (m) => typeof m !== "string"
          );
          if (selectedIdsWithoutNewly.length > 0) {
            await PricingTemplatesPricesApi.DeletePricingTemplatesPrices(
              selectedIdsWithoutNewly
            );
          }
          copy = updated.filter(
            (item) => !selectedIdsForDeletion.includes(item.Id)
          );
          setSelected([]);
        } catch (error) {
          onError(error);
        } finally {
          loader(false);
        }
      }
    }

    setPricingTemplatesPrices(copy);
  };

  const toggleCheckBoxHandler = ({ checked, row }) => {
    const newPricingTemplatePrices = pricingTemplatesPrices.map((pt) => ({
      ...pt,
      BasePriceForChildrenInSeparateRoomCalculation:
        pt.Id === row.Id && checked,
      //If existing one is already checked, uncheck it and have only the newly clicked as checked
    }));

    setPricingTemplatesPrices(newPricingTemplatePrices);
  };

  const createEditableFormatter = (property, digits = 0) => {
    return ({ value, row }) => {
      return (
        <NumericInput
          value={value}
          style={{ width: "100%" }}
          onChangeComplete={(value) =>
            changePricingRow(property, row.Id, value)
          }
          buttonPlacement="none"
          min={0}
          digits={digits}
        />
      );
    };
  };

  const closeModal = (e) => {
    setRowName("");
    setRowNameLocal("");
    setIsActive(true);
    setPricingTemplatesPrices([]);
    onCancelChanges(e);
    onClose?.(e);
  };

  const checkBoxEditableFormatter = ({ value, row }) => {
    return row.Adults == 0 ? (
      <Checkbox
        defaultChecked={value}
        onChange={(e, { checked }) => toggleCheckBoxHandler({ checked, row })}
      />
    ) : null;
  };

  const customPropsCallback = (property) => {
    return (props) => {
      const newProps = { ...props };
      if (!newProps.value) {
        newProps.value = 0;
      }

      if (!newProps.row[property]) {
        newProps.row = { ...newProps.row, [property]: 0 };
      }

      return newProps;
    };
  };

  const gridOptions = {
    columns: [
      {
        name: "Adults",
        title: text("Adults"),
        formatter: createEditableFormatter(propertiesEnum.Adults),
        editor: <NumericInput buttonPlacement="none" min={0} digits={0} />,
        customProps: customPropsCallback(propertiesEnum.Adults),
      },
      {
        name: "Children",
        title: text("Children"),
        formatter: createEditableFormatter(propertiesEnum.Children),
        editor: <NumericInput buttonPlacement="none" min={0} digits={0} />,
        customProps: customPropsCallback(propertiesEnum.Children),
      },
      {
        name: "PriceAdjustment",
        title: text("Percent"),
        formatter: createEditableFormatter(propertiesEnum.PriceAdjustment, 8),
        editor: <NumericInput buttonPlacement="none" min={0} digits={8} />,
        customProps: customPropsCallback(propertiesEnum.PriceAdjustment),
      },
      {
        name: "BasePriceForChildrenInSeparateRoomCalculation",
        title: (
          <Popup
            content={text("SetBasePriceForChildrenInSeparateRoomCalculation")}
            trigger=<p>
              {text("SetBasePriceForChildrenInSeparateRoomCalculation")}
            </p>
          />
        ),
        formatter: checkBoxEditableFormatter,
      },
    ],
    columnExtensions: [
      {
        columnName: "BasePriceForChildrenInSeparateRoomCalculation",
        editingEnabled: false,
      },
    ],
    customOptions: {
      overrideData: true,
    },
    editable: {
      allowAdd: true,
      extraProps: {
        onAddedRowsChange: (addedRows) => {
          setIsAddingRows(addedRows?.length !== 0);
        },
      },
    },
    cacheId: "TemplateEditor",
    paging: true,
    selectable: true,
    showDeleteMultiple: true,
    filtering: true,
    menu: [],
  };
  return (
    <Modal
      closeOnDimmerClick={false}
      closeOnEscape={false}
      className="price-list-settings"
      size="large"
      style={{ maxWidth: "none", width: "60vw" }}
      open={isOpenModal}
      onClose={(e) => {
        closeModal(e);
      }}
      dimmer
      centered
    >
      <Modal.Header>
        {row.Id ? text("EditTemplate") : text("CreateTemplate")}
      </Modal.Header>
      <Modal.Content className="create-template-popup">
        <TemplateForm
          TemplateId={row.Id}
          data={pricingTemplatesPrices}
          onChangeRowName={setRowNameLocal}
          rowName={rowNameLocal}
          setter={setPricingTemplatesPrices}
          isActive={isActive}
          onChangeIsActive={setIsActive}
        />
        <GridPanel
          style={{ overflowY: "auto", height: "45vh" }}
          data={pricingTemplatesPrices}
          options={gridOptions}
          onSelected={setSelected}
          onUpdate={onGridUpdate}
        />
        <Message compact info>{`* ${message(
          "PriceAdjustmentsAreBasedOnDoublePrice"
        )}`}</Message>
      </Modal.Content>
      <Modal.Actions>
        <Button
          negative
          onClick={(e) => {
            closeModal(e);
          }}
        >
          {text("Close")}
        </Button>
        <Button positive onClick={onSave}>
          {text("Save")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const TemplateForm = ({
  TemplateId,
  data,
  setter,
  onChangeRowName,
  rowName,
  isActive,
  onChangeIsActive,
}) => {
  const [maxPax, setMaxPax] = useState(0);
  const [maxAdults, setMaxAdults] = useState(0);
  const [maxChildren, setMaxChildren] = useState(0);
  const text = useTextResources();

  const handleFillButtonClick = () => {
    const fields = {
      Adults: { min: 0, max: maxAdults },
      Children: { min: 0, max: maxChildren },
    };

    const forbiddenCombinations = [{ Adults: 0, Children: 0 }];
    const extraFields = {
      PriceAdjustment: 0,
      TemplateId: TemplateId,
      Id: () => `New-${crypto.randomUUID()}`,
    };

    const maxPaxConstraint = (combination) =>
      combination.Adults + combination.Children <= maxPax;

    const possibilities = getCombinationsPossibilities({
      fields,
      existingCombinations: data,
      constraintFunc: maxPaxConstraint,
      extraFields,
      forbiddenCombinations,
    });

    setter([...data, ...possibilities]);
  };

  return (
    <Form style={{ marginBottom: "2rem" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: "1rem", marginBottom: "1rem" }}>
        <Form.Field required style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="TemplateName">{text("TemplateName")}</label>
          <TextInput
            id="TemplateName"
            value={rowName}
            onChange={onChangeRowName}
          />
        </Form.Field>
        <div className="two wide field checkbox-wrapper">
          {text("Active")}
          <CheckBoxToggle
            onClick={() => onChangeIsActive(!isActive)}
            checked={isActive}
          />
        </div>
      </div>
      <Grid columns={4} stackable>
        <Grid.Row>
          <Grid.Column width={3}>
            <Form.Field>
              <label htmlFor="MaxPax">{text("MaxPax")}</label>
              <NumericInput
                id="MaxPax"
                value={maxPax}
                onChange={(value) => setMaxPax(parseInt(value))}
                buttonPlacement="right"
                min={0}
                digits={0}
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column width={3}>
            <Form.Field>
              <label htmlFor="MaxAdults">{text("MaxAdults")}</label>
              <NumericInput
                id="MaxAdults"
                value={maxAdults}
                onChange={(value) => setMaxAdults(parseInt(value))}
                buttonPlacement="right"
                min={0}
                digits={0}
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column width={3}>
            <Form.Field>
              <label htmlFor="MaxChildren">{text("MaxChildren")}</label>
              <NumericInput
                id="MaxChildren"
                value={maxChildren}
                onChange={(value) => setMaxChildren(parseInt(value))}
                buttonPlacement="right"
                min={0}
                digits={0}
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column width={3} />{" "}
          {/*Empty column for spacing between the button and the inputs*/}
          <Grid.Column style={{ alignSelf: "end" }}>
            <Button primary onClick={handleFillButtonClick}>
              {text("Fill")}
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Form>
  );
};

TemplateEditor.propTypes = {
  open: PropTypes.object,
  row: PropTypes.object,
  onCancelChanges: PropTypes.func,
  setTemplates: PropTypes.func,
  extendedProps: PropTypes.object,
};

TemplateForm.propTypes = {
  TemplateId: PropTypes.number,
  data: PropTypes.array,
  setter: PropTypes.func,
  onChangeRowName: PropTypes.func,
  rowName: PropTypes.string,
  isActive: PropTypes.bool,
  onChangeIsActive: PropTypes.func,
};

export default TemplateEditor;
